/**
 * Overlay close-driver — engine-agnostic primitives for the two close UX
 * behaviours that should fire for ANY dismissible overlay, not just Bootstrap
 * modals: **Escape closes** and **browser-Back closes** (the same-URL pushState
 * pattern our modals use).
 *
 * Why this exists (DRY + the "light-touch" gallery unification):
 *   The unified `.datte-modal` stack gets Escape from Bootstrap itself and
 *   browser-Back from `modal-history-back.ts` (which is bound to the
 *   `*.bs.modal` lifecycle + the engine seam). The parent product-image
 *   lightbox (`body.lightbox-shown`, jtl.gallery.js) is **NOT a Bootstrap
 *   modal** — it has no `.modal`, no `*.bs.modal` events, no engine. To give it
 *   the same close UX WITHOUT forking the parent gallery, the back-to-close
 *   state machine had to be lifted out of its Bootstrap assumptions into a
 *   plain `close()` callback driven by explicit `open()/closed()` signals.
 *
 * `modal-history-back.ts` is the BS-bound adapter over `setupHistoryBackToClose`
 * here (it translates the engine's show/hidden lifecycle into open/closed
 * signals); the gallery adapter drives the SAME primitive from a MutationObserver
 * on `body.lightbox-shown`. One state machine, two drivers — no second
 * back-to-close implementation (the divergence ADR-0007/0008 set out to avoid).
 *
 * Both primitives are pure of any framework probe (no jQuery / bootstrap /
 * eModal) — they take a `close` callback and lifecycle signals, nothing else —
 * so they unit-test in happy-dom with a `vi.fn()` close.
 *
 * Back-to-close is NOT inert on product-detail pages: the pop that retires our
 * entry is indistinguishable, to the parent's `jtl.article.js`, from a user
 * Back-press, so it answered by re-rendering `#result-wrapper` — wiping the
 * customer's configurator selection on every close. While our entry exists we
 * hold an overlay-entry lease; `article-rerender-guard.ts` suppresses the parent's
 * re-render for exactly that long. See those two modules for the full story.
 *
 * @see src-components/history/overlay-entry-lease.ts (why we hold a lease)
 * @see src-components/history/article-rerender-guard.ts (what the lease suppresses)
 * @see docs/architecture/modals/target-architecture.md §3.2.3 (history-back)
 * @see docs/architecture/modals/jtl-modal-inventory.md §D4 (gallery light-touch)
 */

import { acquireOverlayEntryLease, releaseAfterPop } from '../history/overlay-entry-lease';
import type { Unbind } from '../utils/unbind';

/** True for the Escape key, tolerant of the legacy `keyCode` path. */
function isEscapeKey(e: KeyboardEvent): boolean {
  return e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27;
}

/**
 * Wire "Escape closes the overlay". Only fires `close()` when `isOpen()` reports
 * the overlay is currently open, so a single document-level listener can guard a
 * lazily-opened overlay without re-binding per open/close. Returns an `Unbind`
 * that removes the listener.
 *
 * Pure of framework probes: the caller supplies `isOpen` (e.g. a body-class
 * check) and `close` (e.g. click the native close button).
 */
export function setupEscapeToClose(isOpen: () => boolean, close: () => void): Unbind {
  const onKeydown = (e: KeyboardEvent) => {
    if (!isEscapeKey(e)) return;
    if (!isOpen()) return;
    close();
  };
  document.addEventListener('keydown', onKeydown);
  return () => document.removeEventListener('keydown', onKeydown);
}

/**
 * The state machine behind "browser-Back closes the overlay (URL stays clean)".
 *
 * On `open()` it pushes a SAME-URL history entry and starts listening for
 * `popstate`; a Back-press pops that entry → fires `close()`. On `closed()`
 * (the overlay was dismissed by any OTHER means — Escape, the X, a backdrop
 * click) it pops the still-pending entry back off via `history.back()` so the
 * address bar is never left with a stale entry. Identical mechanism to
 * `modal-history-back.ts`; lifted here so the gallery overlay reuses it verbatim.
 *
 * The returned object is wired fire-and-forget by the driver: the BS adapter
 * calls `open`/`closed` from the engine's show/hidden lifecycle; the gallery
 * adapter calls them from a `body.lightbox-shown` MutationObserver. `destroy()`
 * removes the popstate listener for full teardown (the gallery lives on a SPA-ish
 * detail page, so an explicit teardown hook is provided even though detail pages
 * usually full-reload).
 *
 * `release()` is the "overlay hands off to a sibling overlay" seam (the #5 →
 * mini-cart case, ADR-0008 Decision-History): an overlay that is about to trigger
 * a SIBLING overlay (which pushes its OWN history entry) must surrender its own
 * entry FIRST, while the stack still only holds `[…, this-entry]`. Otherwise the
 * sibling pushes on top, and this overlay's later programmatic close pops the
 * SIBLING's entry instead of its own — closing the sibling (the "mini-cart
 * flickers open then shut" bug). `release()` pops this overlay's entry and stops
 * listening, then runs `onSettled` once the pop has LANDED — so the caller opens
 * the sibling on a clean stack. The subsequent `closed()` from the overlay's own
 * close is a no-op (the entry is already gone).
 */
export interface HistoryBackToClose {
  open(): void;
  closed(): void;
  /**
   * Surrender this overlay's history entry NOW (before a sibling overlay pushes
   * its own), then invoke `onSettled` once the pop has landed. If no entry is
   * pending, `onSettled` runs synchronously. Idempotent.
   */
  release(onSettled?: () => void): void;
  destroy(): void;
}

export function setupHistoryBackToClose(
  close: () => void,
  stateTag: Record<string, unknown> = {}
): HistoryBackToClose {
  let statePushed = false;
  // Held for as long as our history entry exists, so the parent does not mistake
  // the pop that retires it for a navigation (see overlay-entry-lease.ts).
  let releaseLease: Unbind | null = null;

  /** Drop the lease one task after the pop, once every popstate listener has run. */
  const releaseLeaseAfterPop = () => {
    if (!releaseLease) return;
    releaseAfterPop(releaseLease);
    releaseLease = null;
  };

  const removeBackListener = () => {
    window.removeEventListener('popstate', onBackNavigation);
  };

  const onBackNavigation = () => {
    if (!statePushed) return;
    statePushed = false;
    removeBackListener();
    releaseLeaseAfterPop();
    close();
  };

  return {
    open() {
      if (statePushed) return; // idempotent — a double open-signal must not stack entries
      // SAME-URL entry (no hash): Back still pops it and fires popstate (→ close),
      // but the address bar stays clean. Pushed on the open signal so a quick
      // back-press during any open animation finds statePushed=true.
      releaseLease = acquireOverlayEntryLease();
      history.pushState({ datteOverlayBack: true, ...stateTag }, '');
      statePushed = true;
      window.addEventListener('popstate', onBackNavigation);
    },
    closed() {
      removeBackListener();
      if (statePushed) {
        statePushed = false;
        // Pop our still-pending entry so Back doesn't later land on a no-op URL.
        // history.back() is ASYNC, so the lease is dropped from the pop it
        // schedules rather than on the next statement.
        const onSettlePop = () => {
          window.removeEventListener('popstate', onSettlePop);
          releaseLeaseAfterPop();
        };
        window.addEventListener('popstate', onSettlePop);
        history.back();
      }
    },
    release(onSettled?: () => void) {
      // Stop reacting to Back FIRST: the upcoming history.back() fires a global
      // popstate that our own onBackNavigation must NOT mistake for a user Back.
      removeBackListener();
      if (!statePushed) {
        // Nothing pending (never opened, or already released/closed) — the stack
        // is already clean for the sibling, so settle synchronously.
        onSettled?.();
        return;
      }
      statePushed = false;
      // history.back() is ASYNC: it schedules a popstate, it does not pop inline.
      // The sibling overlay must push onto a CLEAN stack, so we must not let the
      // caller open it until our entry has actually been popped. Register a
      // one-shot popstate that fires onSettled (and removes itself) once the pop
      // lands, THEN trigger the pop. This makes "release then open sibling"
      // ordered w.r.t. the history stack despite the async pop (no race).
      const onSettlePop = () => {
        window.removeEventListener('popstate', onSettlePop);
        // The sibling overlay takes its OWN lease inside onSettled, so the count
        // goes 1 → 2 → 1 with no gap and nothing has to be ordered against it.
        releaseLeaseAfterPop();
        onSettled?.();
      };
      window.addEventListener('popstate', onSettlePop);
      history.back();
    },
    destroy() {
      removeBackListener();
      statePushed = false;
      // Our entry stays on the stack (teardown cannot pop it safely), but the
      // lease must not outlive the driver — a stuck lease would suppress genuine
      // article navigation for the rest of the page's life.
      releaseLease?.();
      releaseLease = null;
    },
  };
}
